import React, { useState } from 'react';
import {
  Badge,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import LaunchIcon from '@mui/icons-material/Launch';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { useNavigate } from 'react-router-dom';
import { useLineChatAlertContext } from '../../../../../contexts/LineChatAlertContext';

const LINE_CHAT_PATH = '/customer/line-chat-history';

export default function LineChatAlertMenu() {
  const navigate = useNavigate();
  const {
    hasLineChatPermission,
    unreadConversationCount,
    attentionRequiredCount,
    staleUnreadCount,
    latestUnreadConversation,
    isLoading,
    notificationPermission,
    browserNotificationSupported,
    requestBrowserPermission,
    refetch,
  } = useLineChatAlertContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const unreadBadgeColor = staleUnreadCount > 0 ? 'error' : 'warning';

  if (!hasLineChatPermission) return null;

  return (
    <>
      <Tooltip
        title={
          unreadConversationCount > 0
            ? `มีห้องยังไม่อ่าน ${unreadConversationCount} ห้อง`
            : 'ไม่มีข้อความใหม่จาก LINE'
        }
      >
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          sx={{ p: 0.6 }}
        >
          <Badge
            color={unreadBadgeColor}
            badgeContent={unreadConversationCount}
            max={99}
            overlap="circular"
          >
            <MarkChatUnreadOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { width: 300, p: 1.1 } }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            สถานะไลน์แชท
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              size="small"
              color={unreadConversationCount > 0 ? 'warning' : 'default'}
              label={`ยังไม่อ่าน ${unreadConversationCount} ห้อง`}
              sx={{ height: 22, fontSize: '0.72rem' }}
            />
            <Chip
              size="small"
              color={attentionRequiredCount > 0 ? 'error' : 'default'}
              label={`ต้องติดตาม ${attentionRequiredCount}`}
              sx={{ height: 22, fontSize: '0.72rem' }}
            />
            {staleUnreadCount > 0 && (
              <Chip
                size="small"
                color="error"
                label={`ค้างเกิน 10 นาที ${staleUnreadCount}`}
                sx={{ height: 22, fontSize: '0.72rem' }}
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" noWrap>
            ล่าสุด: {latestUnreadConversation?.branch_name || '-'}
          </Typography>

          <Divider />

          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="small"
              startIcon={<LaunchIcon />}
              onClick={() => {
                navigate(LINE_CHAT_PATH);
                setAnchorEl(null);
              }}
            >
              เปิดหน้าแชท
            </Button>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                refetch();
              }}
              disabled={isLoading}
            >
              รีเฟรช
            </Button>
          </Stack>

          {browserNotificationSupported && (
            <Button
              variant="text"
              size="small"
              startIcon={<NotificationsActiveOutlinedIcon />}
              onClick={() => {
                requestBrowserPermission();
              }}
              disabled={notificationPermission === 'granted'}
            >
              {notificationPermission === 'granted'
                ? 'เปิดแจ้งเตือนเบราว์เซอร์แล้ว'
                : 'เปิดแจ้งเตือนเบราว์เซอร์'}
            </Button>
          )}

          {!browserNotificationSupported && (
            <Typography variant="caption" color="text.secondary">
              เบราว์เซอร์นี้ไม่รองรับ Notification API
            </Typography>
          )}
        </Stack>
      </Menu>
    </>
  );
}
